import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useBi } from '@components/Hooks/useBi';
import StudioBadgeSvg from '@components/Images/StudioBadgeSvg';
import { KeyCodeEnter } from '@utils/constants';
import { fillProfileUrl } from '@utils/urlUtils';
import { PortfolioItem } from '@wix/ambassador-partner-profiles/types';
import { copyOfExploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { Box, Image, MediaOverlay, Proportion, Text } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { useCallback, useMemo } from 'react';
import s from './Project.module.scss';

export interface PortfolioItemWithUrl extends PortfolioItem {
  url?: string;
  isStudio?: boolean;
  isMobileView?: boolean;
  layoutType?: 'twoColumn' | 'threeColumn';
}

export const generateThumbnailUrl = (
  metaSiteId: string,
  w: number,
  h: number,
) => `https://www.wix.com/site-thumbnail/${metaSiteId}?w=${w}&h=${h}`;

const Project = ({
  customImage,
  description,
  metaSiteId,
  siteName,
  url,
  isStudio,
  isMobileView,
  layoutType = 'threeColumn',
}: PortfolioItemWithUrl) => {
  const { t } = useTranslation();
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { entry, origin } = useProfileContext();
  const { experiments } = useExperiments({ readOnly: true });

  const mediaSources = useMemo(() => {
    const dimensions =
      layoutType === 'twoColumn'
        ? { width: 665, height: 445 }
        : { width: 450, height: 300 };

    const generatedThumbnailSrc = metaSiteId
      ? generateThumbnailUrl(metaSiteId, dimensions.width, dimensions.height)
      : '';

    const mediaSource = customImage
      ? fillProfileUrl(customImage, dimensions.width, dimensions.height)
      : generatedThumbnailSrc;

    const backupSource = customImage ? generatedThumbnailSrc : '';
    return { mediaSource, backupSource };
  }, [metaSiteId, customImage, layoutType]);

  const onClickLink = useCallback(() => {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'project website',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({ metaSiteId }),
      }),
    );
  }, [sessionId, origin, entry, metaSiteId]);

  return (
    <div
      data-hook="portfolio-project"
      className={isMobileView ? s.projectMobile : s.project}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.keyCode === KeyCodeEnter && url) {
          onClickLink();
          window.open(url, '_blank');
        }
      }}
    >
      <Proportion aspectRatio={3 / 2} className={s.proportion}>
        <MediaOverlay
          hoverSkin="gradient"
          borderRadius="3px 3px 0 0"
          className={s.mediaOverlay}
          media={
            <Box height="100%" className={s.imageWrapper}>
              <Image
                className={s.projectImage}
                fit="fill"
                dataHook="project-media-image"
                alt={`${siteName}: ${description}`}
                borderRadius="3px 3px 0 0"
                src={mediaSources.mediaSource}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  if (mediaSources.backupSource) {
                    e.currentTarget.src = mediaSources.backupSource;
                  }
                }}
              />
              {isStudio ? (
                <Box
                  dataHook="project-studio-badge"
                  position="absolute"
                  right="6px"
                  bottom="6px"
                  zIndex="1"
                >
                  <StudioBadgeSvg />
                </Box>
              ) : null}
            </Box>
          }
          dataHook="project-media-overlay"
        >
          <MediaOverlay.Content
            placement="bottom-start"
            visible="hover"
            data-hook="project-media-overlay-content"
          >
            <Box className={s.hoverContent}>
              <Text size="small" weight="normal" className={s.projectName}>
                {siteName}
              </Text>
            </Box>
          </MediaOverlay.Content>
        </MediaOverlay>
      </Proportion>
    </div>
  );
};

export default Project;
